import * as ReactDOM from 'react-dom/client';
import * as React from 'react';
import { WebComponent, UIRuntimeBuilder } from '@cian/mf-registry/browser';
import { IApplicationContext } from 'shared/types/applicationContext';
import { TReduxStore } from 'shared/types/redux';
import { Application } from 'shared/containers/App';

interface IHydrationGuardProps {
  children: React.ReactNode;
  onHydrated(): void;
}

const HydrationGuard: React.FC<IHydrationGuardProps> = ({ children, onHydrated }) => {
  React.useEffect(() => {
    onHydrated();
  }, [onHydrated]);

  return <>{children}</>;
};

export const createMicrofrontendUI =
  (context: IApplicationContext, store: TReduxStore, uiRuntimeName: string) => (): UIRuntimeBuilder => {
    class SearchString extends WebComponent {
      public constructor() {
        super();
      }
      private disconnectTimerId: ReturnType<typeof setTimeout> | null = null;
      private isHydrating = false;
      private pendingRenderTimerId: ReturnType<typeof setTimeout> | null = null;
      private pendingReactElement: React.ReactElement | null = null;
      private root: ReactDOM.Root | null = null;
      protected microfrontendName = context.config.getStrict<string>('projectName');
      protected imageVersion = context.config.getStrict<string>('version.commit');
      protected runtimeName = uiRuntimeName;

      private clearPendingRenderTimer = () => {
        if (this.pendingRenderTimerId !== null) {
          clearTimeout(this.pendingRenderTimerId);
          this.pendingRenderTimerId = null;
        }
      };

      private flushPendingReactElement = () => {
        this.pendingRenderTimerId = null;

        const pendingReactElement = this.pendingReactElement;
        this.pendingReactElement = null;

        if (pendingReactElement && this.root) {
          this.root.render(pendingReactElement);
        }
      };

      private schedulePendingRender = () => {
        if (!this.pendingReactElement || this.pendingRenderTimerId !== null) {
          return;
        }

        this.pendingRenderTimerId = setTimeout(this.flushPendingReactElement, 0);
      };

      private handleHydrated = () => {
        if (!this.isHydrating) {
          return;
        }

        this.isHydrating = false;
        this.schedulePendingRender();
      };

      private createReactElement(): React.ReactElement {
        return (
          <HydrationGuard onHydrated={this.handleHydrated}>
            <Application {...this.props} context={context} reduxStore={store} />
          </HydrationGuard>
        );
      }

      public async connectedCallback() {
        if (this.disconnectTimerId) {
          clearTimeout(this.disconnectTimerId);
          this.disconnectTimerId = null;
        }

        await super.connectedCallback();

        if (!this.isHydrating) {
          this.schedulePendingRender();
        }
      }

      public disconnectedCallback() {
        if (this.disconnectTimerId) {
          clearTimeout(this.disconnectTimerId);
        }

        this.clearPendingRenderTimer();

        this.disconnectTimerId = setTimeout(() => {
          if (this.isConnected) {
            this.disconnectTimerId = null;

            return;
          }

          this.clearPendingRenderTimer();
          this.isHydrating = false;
          this.pendingReactElement = null;
          this.root?.unmount();
          this.root = null;
          super.disconnectedCallback();
          this.disconnectTimerId = null;
        }, 0);
      }

      public async render() {
        const reactElement = this.createReactElement();

        if (!this.root) {
          if (this.shouldHydrate) {
            this.isHydrating = true;
            this.root = ReactDOM.hydrateRoot(this, reactElement);
          } else {
            this.root = ReactDOM.createRoot(this);
            this.root.render(reactElement);
          }
        } else if (this.isHydrating) {
          this.pendingReactElement = reactElement;
        } else if (this.pendingRenderTimerId !== null) {
          this.pendingReactElement = reactElement;
        } else {
          this.root.render(reactElement);
        }
      }
    }

    return { customElementConstructor: SearchString };
  };
